/**
 * option-steps.js — 段階選択オプション UI（施策B: オプションUIのビジュアル化）
 *
 * ■ 何をするものか
 * バリアントメタフィールド（opt_tc / opt_cup / opt_pb / opt_hs）を持つ商品の選択肢を、
 * 部品ごとの段階選択 UI として描き直す。選択されたら、Dawn 標準のバリアントピッカーの
 * コントロール（radio / select）を代理操作して元の選択に戻す。
 *
 * ■ 設計方針（2026-08-16 メタフィールド化拡張）
 * - 判定はメタフィールドのタプル集合からボトムアップで組み立てる。軸の値集合の直積は
 *   使わない。UI が提示する選択肢は「実在するバリアントが持つタプル」そのものなので、
 *   「選択肢はあるが対応する実バリアントが無い」状態（ダングリング選択）が構造的に
 *   発生しない
 * - 各軸の意味（色・個数の解釈、どのステップに分解するか）は固定のデコード表
 *   （AXIS_DECODE）としてコードに持つ。値は Liquid が渡す4種の許容値のみで、
 *   自由入力の文字列を解析することはない（旧実装の正規表現パーサーは廃止）
 * - 選択コントロールの HTML は自作しない。Dawn 標準の product-variant-picker を使う
 *   → テーマ更新時に Dawn 側の仕様変更へ自動で追随する
 * - 操作対象は2モード（どちらかは Liquid 側が判定して data-mode で渡す）
 *   standalone: コントロールがこのブロックの内側にある（variant_picker ブロックが無い場合）
 *   drive:      別の variant_picker ブロックが出したコントロールを操作する
 * - 状態を保持しない。表示は毎回「コントロールで今何が選ばれているか」から導出する
 * - 見出しの文言は部品名をキーにせず [部品] の雛形で置換する
 */
(function () {
  'use strict';

  /* ========================================================================
   * 軸のデコード表（固定・コードに焼き込む）
   *
   * Liquid から渡される値は custom.opt_tc / opt_cup / opt_pb / opt_hs の
   * 許容値（choices固定のメタフィールド）のみ。自由入力ではないため文字列解析は不要。
   * 値が null の項目は「その軸を追加しない」状態を表す。
   * ======================================================================== */

  var AXIS_LABELS = {
    opt_tc: 'テーブルコンテナ',
    opt_cup: 'カップホルダー',
    opt_pb: 'ペダルブレーキ',
    opt_hs: 'ゴム紐'
  };

  var AXIS_DECODE = {
    opt_tc: {
      none: null,
      gray_1: { color: 'グレー', qty: '1個' },
      gray_2: { color: 'グレー', qty: '2個' },
      black_1: { color: 'ブラック', qty: '1個' },
      black_2: { color: 'ブラック', qty: '2個' }
    },
    opt_cup: {
      none: null,
      '2': { qty: '2個セット' }
    },
    opt_pb: {
      '0': null,
      '1': {}
    },
    opt_hs: {
      '0': null,
      '1': { qty: '1本' }
    }
  };

  /**
   * 軸ごとの選択肢モデルを、実在バリアントのタプル集合から直接組み立てる
   * （軸の値集合の直積は使わない＝ボトムアップ構築）。
   * @param {string[]} axisKeys 有効軸（Liquid が渡す順）
   * @param {Array} variants 各バリアントの { tuple, value, ... }
   */
  function buildModel(axisKeys, variants) {
    var axes = axisKeys.map(function (key) {
      var decodeTable = AXIS_DECODE[key] || {};
      var presentRaw = {};
      variants.forEach(function (v) {
        presentRaw[v.tuple[key]] = true;
      });
      var noneRaw = null;
      var choices = [];
      var seen = {};
      Object.keys(decodeTable).forEach(function (raw) {
        if (!presentRaw[raw]) return; // この商品に実在する値だけを候補にする
        var decoded = decodeTable[raw];
        if (decoded === null) {
          noneRaw = raw;
          return;
        }
        var k = (decoded.color || '') + '|' + (decoded.qty || '');
        if (seen[k]) return;
        seen[k] = true;
        choices.push({ raw: raw, color: decoded.color || '', qty: decoded.qty || '' });
      });
      return { key: key, label: AXIS_LABELS[key] || key, choices: choices, noneRaw: noneRaw };
    });

    // タプル → バリアント の対応表を実在バリアントから直接構築する。
    // 提示する選択肢の集合＝この map のキー集合そのものになるため、
    // ダングリング選択（キーはあるが実体が無い状態）は構造的に発生しない
    var map = {};
    variants.forEach(function (v) {
      map[comboKeyFromTuple(axisKeys, v.tuple)] = v;
    });

    return { axes: axes, map: map, axisKeys: axisKeys };
  }

  function comboKeyFromTuple(axisKeys, tuple) {
    return axisKeys
      .map(function (k) {
        return k + '=' + tuple[k];
      })
      .join('&');
  }

  /* ========================================================================
   * 商品組み合わせパターンの分類（2026-08-16）
   *
   * ■ 設計原則: 「パターンを分類し、分類結果によってその後の見せ方（ステップ構成）を
   *   変える」方式にすることで、バリアントピッカーの実装を1つに一元管理できる。
   *   複数の別実装（別ブロック・別スニペット）に分岐させない。
   *
   * 「テーブルコンテナを追加しないと色・個数が選べないが、カップホルダーは
   * 独立して選択できる」といった軸どうしの依存関係は、軸の性質（色があるか・
   * 個数の種類が複数あるか）から暗黙的に導出するのではなく、ここで商品組み合わせ
   * パターンとして名前を付けて明示的に分岐する（build() 内の switch）。
   *
   * 将来、軸間の依存関係が今と異なる新しい組み合わせ（例: 軸Aを追加しないと軸Bも
   * 選べない、等）が出てきたら:
   *   1. このバリアントピッカー自体は増やさない（1つのまま）
   *   2. PATTERNS に新しいパターン名と軸の組み合わせを追加する
   *   3. build() の switch に、その新パターン専用の分岐を追加する
   *      （既存パターンの分岐・挙動には手を入れない＝既存商品への影響ゼロ）
   *
   * 既知のどのパターンにも一致しない軸の組み合わせは、推測でステップ構成を
   * 組まず、安全側（通常ピッカー）にフォールバックする。
   * ======================================================================== */

  var PATTERNS = {
    TC_ONLY: ['opt_tc'],
    TC_CUP: ['opt_tc', 'opt_cup'],
    PB_ONLY: ['opt_pb'],
    PB_HS: ['opt_pb', 'opt_hs'],
    HS_ONLY: ['opt_hs']
  };

  function classifyPattern(axisKeys) {
    var sorted = axisKeys.slice().sort().join(',');
    for (var name in PATTERNS) {
      if (PATTERNS[name].slice().sort().join(',') === sorted) return name;
    }
    return null;
  }

  /* ========================================================================
   * ギャラリーの自動スクロール抑止
   * ======================================================================== */

  /**
   * バリアント変更のたびにページが上へ飛ぶのを止める。
   *
   * ■ 何が起きていたか
   * Dawn はバリアントが変わるとメイン画像を差し替え、その画像が画面の上に隠れていると
   * 画像が見える位置までページ全体を自動スクロールする（assets/media-gallery.js の
   * setActiveMedia 内。`activeMediaRect.top > -0.5` で「既に見えている」ときだけ抑止され、
   * それ以外は必ず window.scrollTo が走る）。
   * 段階選択UIは「追加するか → カラー → 個数」と複数回クリックするため、押すたびに
   * 画面が上へ飛び、続きの選択がしにくくなる。
   *
   * ■ テーマ設定では止められない
   * ギャラリー関連の設定（media_size / gallery_layout / mobile_thumbnails /
   * hide_variants / enable_video_looping）はどれもこの縦スクロールに関与しない。
   * Dawn 側にハードコードされているため、上書きするしかない。
   *
   * ■ やり方と、その理由
   * setActiveMedia を包み、その処理中だけ window.scrollTo を無効化する。
   * - Dawn は setTimeout の中で window.scrollTo を呼ぶ。復元の setTimeout を
   *   orig の呼び出し「後」に登録することで、抑止したい1tickだけを外して自動で元に戻る
   * - 「スクロールしてから戻す」方式は smooth 動作と競合して画面が揺れるため採らない
   * - 無効化するのは window.scrollTo だけ。サムネイルの横送りは要素側の
   *   `activeMedia.parentElement.scrollTo` なので影響しない
   *
   * ■ 適用範囲
   * この関数は**段階選択UIの描画に成功した商品でだけ**呼ぶ。従来の選択肢一覧で
   * 表示している商品（メタフィールド未投入の68商品）では呼ばれないため、
   * Dawn 標準の挙動がそのまま残る。
   *
   * ■ トレードオフ（意図した上での選択）
   * 画像が画面外にある状態で色を変えても、画像の変化がその場では見えなくなる。
   * 段階選択を最後まで進められることを優先した（2026-08-27 判断）。
   */
  function suppressGalleryPageScroll() {
    // ★このファイルは media-gallery.js より先に読み込まれる（Dawn のスクリプト順）。
    // そのため呼ばれた時点では media-gallery がまだ未定義で、素朴に
    // customElements.get() を見るだけだと常に空振りする。定義を待ってから仕掛ける。
    // バリアント変更はユーザー操作なので、読み込み中に解決すれば十分間に合う
    if (customElements.get('media-gallery')) {
      patchMediaGallery();
    } else if (typeof customElements.whenDefined === 'function') {
      customElements.whenDefined('media-gallery').then(patchMediaGallery);
    }
  }

  function patchMediaGallery() {
    var MediaGalleryClass = customElements.get('media-gallery');
    if (!MediaGalleryClass) return;
    var proto = MediaGalleryClass.prototype;
    if (proto.__optionStepsScrollPatched) return; // 多重適用を防ぐ
    if (typeof proto.setActiveMedia !== 'function') return;

    proto.__optionStepsScrollPatched = true;
    var originalSetActiveMedia = proto.setActiveMedia;

    proto.setActiveMedia = function () {
      var realScrollTo = window.scrollTo;
      var stub = function () {};
      window.scrollTo = stub;
      try {
        return originalSetActiveMedia.apply(this, arguments);
      } finally {
        // orig が内部で登録した setTimeout より後に登録されるため、
        // Dawn のスクロールを1tickだけ空振りさせてから元に戻る
        window.setTimeout(function () {
          if (window.scrollTo === stub) window.scrollTo = realScrollTo;
        });
      }
    };
  }

  /* ========================================================================
   * 既存のバリアントピッカーとの接続
   * ======================================================================== */

  /** 標準ピッカーの中から、指定のオプション値に対応するコントロールを探す */
  function findControl(scope, value) {
    var inputs = scope.querySelectorAll('fieldset input[type="radio"]');
    for (var i = 0; i < inputs.length; i++) {
      if (inputs[i].value === value) return { type: 'radio', el: inputs[i] };
    }
    var selects = scope.querySelectorAll('select');
    for (var j = 0; j < selects.length; j++) {
      var opts = selects[j].options;
      for (var k = 0; k < opts.length; k++) {
        if (opts[k].value === value) return { type: 'select', el: selects[j], option: opts[k] };
      }
    }
    return null;
  }

  /** 標準ピッカーで現在選ばれているオプション値の文字列を読む */
  function currentValue(scope) {
    var checked = scope.querySelector('fieldset input[type="radio"]:checked');
    if (checked) return checked.value;
    var select = scope.querySelector('select');
    if (select) return select.value;
    return null;
  }

  /**
   * 標準ピッカーを代理操作する。
   * change を bubbles: true で発火させることで、Dawn の変更検知（variant-selects の
   * change リスナー）に人のクリックと同じ形で届き、標準の更新パイプラインが走る。
   */
  function drive(scope, value) {
    var ctrl = findControl(scope, value);
    if (!ctrl) return false;
    if (ctrl.type === 'radio') {
      if (ctrl.el.checked) return true;
      ctrl.el.checked = true;
      ctrl.el.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      if (ctrl.el.value === value) return true;
      ctrl.el.value = value;
      ctrl.el.dispatchEvent(new Event('change', { bubbles: true }));
    }
    return true;
  }

  /* ========================================================================
   * カスタム要素
   * ======================================================================== */

  class OptionSteps extends HTMLElement {
    connectedCallback() {
      // 標準ピッカーがまだ DOM に無い可能性がある。
      // このブロックを variant_picker より前に配置された場合、connectedCallback の時点では
      // 標準ピッカーが未出力のため、DOM の構築完了を待ってから判定する
      if (!this.scope && document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.init(), { once: true });
        return;
      }
      this.init();
    }

    init() {
      if (this.initialized) return;
      this.initialized = true;

      var dataEl = this.querySelector('[data-option-steps-data]');
      this.ui = this.querySelector('[data-option-steps-ui]');
      if (!dataEl || !this.ui) return;

      // 標準ピッカーが無ければ UI を出さない。
      // この UI は標準ピッカーの radio / select を代理操作する仕組みなので、
      // 相手が居ないと「見えるのにクリックが効かない」状態になる。それは出さない方がよい
      if (!this.scope) {
        this.giveUp(
          'このブロックが操作する対象（バリエーションピッカー）が見つかりませんでした。' +
            'テーマエディタで「バリエーションピッカー」ブロックを、このブロックより前に配置してください'
        );
        return;
      }

      try {
        this.data = JSON.parse(dataEl.textContent);
      } catch (e) {
        this.giveUp('データを読み取れませんでした', e);
        return;
      }

      var axisKeys = this.data.axes || [];
      if (!axisKeys.length || !this.data.variants || !this.data.variants.length) {
        this.giveUp('有効なオプション軸が見つかりませんでした');
        return;
      }

      this.model = buildModel(axisKeys, this.data.variants);

      this.build();
      this.ui.hidden = false;

      // 段階選択UIが確実に描画できた後にだけ選択コントロールを隠す。
      // 先に隠してしまうと、読み取りに失敗した時に選択肢が一切表示されず購入できなくなる
      this.applyNativeVisibility();

      // 描画に成功した商品でだけ、バリアント変更時のページ自動スクロールを止める。
      // 段階選択は複数回クリックするため、そのたびに画面が上へ飛ぶと選択を続けられない
      suppressGalleryPageScroll();

      // テーマエディタでは読み取り結果を表示する。
      // メタフィールドから何を部品・カラー・個数として認識したかが見えないと、
      // 値を変えた時に「意図した変化か、崩れたのか」を判断できない
      if (this.dataset.designMode) this.showRecognized();

      // drive モード（variant_picker と同時配置）は通常の運用形態なので、
      // 公開ページでは何も出さない。テーマエディタでのみ構成を知らせる
      if (this.dataset.mode === 'drive' && this.dataset.designMode) {
        console.info(
          '[option-steps] 「バリエーションピッカー」ブロックと同時に配置されています。' +
            'このブロックはそちらの選択肢を操作する構成で動作し、元のピッカーは自動的に非表示にします。' +
            'このブロック単独で完結させたい場合は「バリエーションピッカー」ブロックを削除してください。'
        );
      }

      this.addEventListener('click', this.onClick.bind(this));

      // Dawn がピッカーを差し替えた後に表示を追従させる
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, () => {
          requestAnimationFrame(() => {
            // 差し替えで新しい要素になるため、非表示指定を毎回付け直す
            this.applyNativeVisibility();
            this.sync();
          });
        });
      }
      this.sync();
    }

    /**
     * 選択コントロールを視覚的に隠す。
     * 段階選択UIが表側の操作手段になるため、どちらのモードでも隠す。
     * DOM からは外さないので、中の radio は選択の実体として機能し続ける
     * （フォーム送信・Dawn の変更検知の対象に残る）。
     * この処理は build() 成功後にだけ呼ぶこと（失敗時は隠さず選択肢を残す）
     */
    applyNativeVisibility() {
      var scope = this.scope;
      if (!scope) return;

      // 準備完了の印。自分の内側にあるコントロール（単独モード）は CSS 側でこの印を見て隠す。
      this.dataset.ready = '1';

      // 代理操作モードでは対象が自分の外にある。
      //
      // ★印を「差し替えられない祖先」に付けるのが要点。
      // Dawn はバリアント変更のたびに <variant-selects> を要素ごと差し替えるため、
      // その要素自身に印を付けると差し替え直後の一瞬だけ印が無い＝見えている状態になる。
      // その隙に Dawn が「最後に押した選択肢」へフォーカスを戻すため
      // （product-info.js の `document.querySelector('#'+targetId)?.focus()`）、
      // 見えているコントロールにフォーカスが移り、そこまでページがスクロールしてしまう。
      // 祖先側の印を CSS で見て子孫を隠せば、差し替え直後の新しい要素も即座に隠れ、
      // フォーカスの復帰先が display:none になるためスクロールも起きない。
      if (!this.contains(scope)) {
        var stable = scope.closest('product-info') || scope.parentElement;
        if (stable) stable.setAttribute('data-option-steps-hides-native', '');
        // 祖先が取れなかった場合の保険（この場合は上記の隙が残る）
        scope.classList.add('option-steps-native-hidden');
      }
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    /** 標準ピッカーの在り処。無ければ UI を出さない */
    get scope() {
      var sel = this.dataset.targetVariantSelects;
      return (sel && document.querySelector(sel)) || null;
    }

    giveUp(reason, err) {
      // 静かに劣化させない。標準ピッカーは残るので購入操作には影響しない
      console.warn(
        '[option-steps] 段階選択UIを表示していません（標準のバリアント選択のみ表示）。理由: ' + reason,
        err || ''
      );
      this.setAttribute('data-option-steps-fallback', reason);
    }

    /** 現在の選択を、軸ごとの生の値（メタフィールド値の文字列）に読み替える */
    state() {
      var scope = this.scope;
      var rawValue = scope ? currentValue(scope) : null;
      var v = this.data.variants.find(function (x) {
        return x.value === rawValue;
      });
      if (!v) return {};
      var parts = {};
      this.model.axisKeys.forEach(function (key) {
        var raw = v.tuple[key];
        if (raw !== undefined) parts[key] = raw;
      });
      return parts;
    }

    /** 各軸の「なし」を補って完全なタプルにし、対応するバリアントを引く */
    lookup(parts) {
      var self = this;
      var full = {};
      this.model.axisKeys.forEach(function (key) {
        full[key] = parts && parts[key] !== undefined ? parts[key] : self.axisNoneRaw(key);
      });
      return this.model.map[comboKeyFromTuple(this.model.axisKeys, full)] || null;
    }

    axisNoneRaw(key) {
      var axis = this.model.axes.find(function (a) {
        return a.key === key;
      });
      return axis ? axis.noneRaw : null;
    }

    /**
     * パターン分類→パターンごとの構築関数へ明示的に分岐する。
     * ここが「1つのバリアントピッカーで一元管理しつつ、商品組み合わせパターンを
     * 冒頭で切り分ける」構造の入口。未知のパターンは安全側にフォールバックする
     */
    build() {
      var axisByKey = {};
      this.model.axes.forEach(function (a) {
        axisByKey[a.key] = a;
      });
      this.stepNo = 0;

      var pattern = classifyPattern(this.model.axisKeys);
      var html;
      switch (pattern) {
        case 'TC_ONLY':
          html = this.renderAxisSteps(axisByKey.opt_tc);
          break;
        case 'TC_CUP':
          // opt_tc と opt_cup は独立（カップホルダーはテーブルコンテナ未追加でも選べる）。
          // 将来「テーブルコンテナ追加が前提」等の依存関係が必要なパターンが出たら、
          // ここに専用の分岐を新設し、このTC_CUPの並置構成は変更しない
          html = this.renderAxisSteps(axisByKey.opt_tc) + this.renderAxisSteps(axisByKey.opt_cup);
          break;
        case 'PB_ONLY':
          html = this.renderAxisSteps(axisByKey.opt_pb);
          break;
        case 'PB_HS':
          // opt_pb と opt_hs も独立（どちらも単独で追加可）
          html = this.renderAxisSteps(axisByKey.opt_pb) + this.renderAxisSteps(axisByKey.opt_hs);
          break;
        case 'HS_ONLY':
          html = this.renderAxisSteps(axisByKey.opt_hs);
          break;
        default:
          // 既知のどのパターンにも一致しない軸の組み合わせ。
          // 推測でステップ構成を組まず、安全側（通常ピッカー）にフォールバックする
          this.giveUp('未対応の軸の組み合わせです: ' + this.model.axisKeys.join(','));
          return;
      }

      html = '<div class="option-steps__list">' + html + '</div>';
      if (this.dataset.showSummary) {
        html += '<div class="option-steps__summary"><span data-summary></span></div>';
      }
      this.ui.innerHTML = html;
    }

    /**
     * 1軸ぶんのステップHTML（追加するか→色→個数）を組み立てる。
     * パターンごとの build 関数から呼ばれる共通部品。色・個数のサブステップは
     * その軸の実際の選択肢に応じて出す/出さないが決まる（軸間の依存はここでは扱わない。
     * 軸間の依存が要るパターンは呼び出し側=パターン別のbuild関数で組む）
     */
    renderAxisSteps(axis) {
      var self = this;
      var ai = this.model.axes.indexOf(axis);
      // 色と個数で判定が非対称なのは意図的（2026-08-27 確認済み。直さないこと）。
      // 色は「1色しかなくても」ステップを出す。ブラック専用セットのような商品でも
      // 「テーブルコンテナのカラーを選ぶ → ブラック」と見えている方が、何色が付いてくるかが
      // 分かって使いやすく、シリーズ内でステップ構成も揃うため。
      // 個数は逆に「1種類ならステップを出さない」。個数は要約に出れば足りる。
      var colorful = axis.choices.some(function (c) {
        return c.color;
      });
      var multiQty = new Set(axis.choices.map(function (c) { return c.qty; })).size > 1;
      var html = '';

      // Step: 追加するかどうか
      this.stepNo++;
      html +=
        '<div class="option-steps__step">' +
        self.headHtml(self.stepNo, self.label('add', axis.label)) +
        '<div class="option-steps__toggle">' +
        '<button type="button" class="option-steps__opt" data-axis="' + ai + '" data-kind="add" data-value="no">追加しない</button>' +
        '<button type="button" class="option-steps__opt" data-axis="' + ai + '" data-kind="add" data-value="yes">追加する' +
        self.deltaHtml(axis, axis.choices[0]) +
        '</button>' +
        '</div></div>';

      // Step: カラー（色の違いがある軸のみ）
      if (colorful) {
        this.stepNo++;
        var colors = [];
        axis.choices.forEach(function (c) {
          if (c.color && colors.indexOf(c.color) === -1) colors.push(c.color);
        });
        html +=
          '<div class="option-steps__step" data-axis="' + ai + '" data-panel="color" hidden>' +
          self.headHtml(self.stepNo, self.label('color', axis.label)) +
          '<div class="option-steps__swatches">' +
          colors
            .map(function (col) {
              // カラーのステップだけ画像付きのスウォッチにする（2026-08-27 復帰）。
              // 「追加するか」「個数」はテキストのまま。色の判別だけを画像で助ける。
              // 画像が取れない色はテキストだけで描画され、選択自体は成立する
              var img = self.imageFor(axis, col);
              return (
                '<button type="button" class="option-steps__sw" data-axis="' + ai + '" data-kind="color" data-value="' + esc(col) + '">' +
                '<span class="option-steps__chip">' +
                (img ? '<img src="' + esc(img) + '" alt="' + esc(col) + '" loading="lazy" width="60" height="60">' : '') +
                '</span><span class="option-steps__nm">' + esc(col) + '</span></button>'
              );
            })
            .join('') +
          '</div></div>';
      }

      // Step: 個数（個数の違いがある軸のみ）
      if (multiQty) {
        this.stepNo++;
        var qtys = [];
        axis.choices.forEach(function (c) {
          if (qtys.indexOf(c.qty) === -1) qtys.push(c.qty);
        });
        html +=
          '<div class="option-steps__step" data-axis="' + ai + '" data-panel="qty" hidden>' +
          self.headHtml(self.stepNo, self.label('qty', axis.label)) +
          '<div class="option-steps__toggle option-steps__toggle--qty">' +
          qtys
            .map(function (q) {
              var c = axis.choices.find(function (x) {
                return x.qty === q;
              });
              return (
                '<button type="button" class="option-steps__opt" data-axis="' + ai + '" data-kind="qty" data-value="' + esc(q) + '">' +
                esc(q) + self.deltaHtml(axis, c) + '</button>'
              );
            })
            .join('') +
          '</div></div>';
      }

      return html;
    }

    headHtml(no, text) {
      return (
        '<div class="option-steps__head"><span class="option-steps__badge" data-badge>' +
        no +
        '</span>' +
        esc(text) +
        '</div>'
      );
    }

    /**
     * ステップ番号を「今表示されているステップだけ」で振り直す。
     * カラー・個数は「追加する」を選ぶまで隠れるため、固定番号だと 1 → 4 のように飛んで
     * 「2 と 3 はどこ？」という見え方になる
     */
    renumber() {
      var n = 0;
      this.ui.querySelectorAll('.option-steps__step').forEach(function (step) {
        if (step.hidden) return;
        n++;
        var badge = step.querySelector('[data-badge]');
        if (badge) badge.textContent = n;
      });
    }

    /**
     * 本体価格との差額を出す。
     * 金額の整形は Liquid 側（money フィルタ）で済ませてあるので、ここでは文字列を使うだけ。
     * JS で桁の計算をしないことで、最小通貨単位の解釈ミス（100倍表示）が起きない
     */
    deltaHtml(axis, choice) {
      if (!choice) return '';
      var parts = {};
      parts[axis.key] = choice.raw;
      var v = this.lookup(parts);
      if (!v || !v.delta) return '';
      return '<span class="option-steps__sub">+' + esc(v.deltaFormatted) + '</span>';
    }

    /**
     * カラースウォッチの画像を引き当てる。
     * 新規に画像を用意するのではなく「その色で最も個数の少ないバリアント」に
     * 設定済みの商品画像を流用する。見つからなければ null を返し、
     * 呼び出し側はテキストのみのスウォッチとして描画する（選択は成立する）
     */
    imageFor(axis, color) {
      var cands = axis.choices.filter(function (c) {
        return c.color === color;
      });
      for (var i = 0; i < cands.length; i++) {
        var parts = {};
        parts[axis.key] = cands[i].raw;
        var v = this.lookup(parts);
        if (v && v.image) return v.image;
      }
      return null;
    }

    /** テーマエディタ専用: メタフィールドから何を読み取ったかを表示する */
    showRecognized() {
      var lines = this.model.axes.map(function (axis) {
        var choices = axis.choices
          .map(function (c) {
            return (c.color ? c.color + ' ' : '') + c.qty;
          })
          .join('、');
        return '<li><strong>' + esc(axis.label) + '</strong>：' + esc(choices) + '</li>';
      });
      var box = document.createElement('div');
      box.className = 'option-steps__notice option-steps__notice--info';
      box.setAttribute('role', 'status');
      box.innerHTML =
        '<strong>編集画面のみの表示：メタフィールドから読み取った内容</strong>' +
        '<ul>' +
        lines.join('') +
        '</ul>' +
        '意図と違う場合は、バリアントのメタフィールド（opt_tc / opt_cup / opt_pb / opt_hs）を確認してください。' +
        '見出しの文言に書いた <code>[部品]</code> には、ここに出ている軸名が入ります。';
      this.ui.insertBefore(box, this.ui.firstChild);
    }

    /**
     * 見出しの文言を組み立てる。
     * 部品名（軸ラベル）をキーにした対応表にはしない。[部品] という雛形を
     * 軸ラベルで置換する形にして、AXIS_LABELS の表記変更に自動で追随させる
     */
    label(kind, partName) {
      var raw = this.dataset['label' + kind.charAt(0).toUpperCase() + kind.slice(1)] || '';
      if (!raw) {
        raw = kind === 'add' ? '[部品]を追加しますか？' : kind === 'color' ? '[部品]のカラーを選ぶ' : '[部品]の個数を選ぶ';
      }
      return raw.replace(/\[部品\]/g, partName).replace(/\[part\]/g, partName);
    }

    onClick(event) {
      var btn = event.target.closest('[data-kind]');
      if (!btn || !this.ui.contains(btn)) return;
      event.preventDefault();

      var axis = this.model.axes[Number(btn.dataset.axis)];
      var parts = Object.assign({}, this.state());
      var curRaw = parts[axis.key];
      var cur = axis.choices.find(function (c) {
        return c.raw === curRaw;
      });

      if (btn.dataset.kind === 'add') {
        if (btn.dataset.value === 'no') parts[axis.key] = axis.noneRaw;
        else if (!cur) parts[axis.key] = axis.choices[0] ? axis.choices[0].raw : axis.noneRaw;
      } else if (btn.dataset.kind === 'color') {
        var wantQty = cur ? cur.qty : null;
        var matchColor =
          axis.choices.find(function (c) {
            return c.color === btn.dataset.value && c.qty === wantQty;
          }) ||
          axis.choices.find(function (c) {
            return c.color === btn.dataset.value;
          });
        parts[axis.key] = matchColor ? matchColor.raw : parts[axis.key];
      } else if (btn.dataset.kind === 'qty') {
        var wantColor = cur ? cur.color : null;
        var matchQty =
          axis.choices.find(function (c) {
            return c.qty === btn.dataset.value && c.color === wantColor;
          }) ||
          axis.choices.find(function (c) {
            return c.qty === btn.dataset.value;
          });
        parts[axis.key] = matchQty ? matchQty.raw : parts[axis.key];
      }

      var target = this.lookup(parts);
      var scope = this.scope;
      if (!target || !scope) {
        this.sync();
        return;
      }
      drive(scope, target.value);
      this.sync();
    }

    /** 表示を現在の選択から描き直す */
    sync() {
      var parts = this.state();
      var self = this;

      this.ui.querySelectorAll('[data-kind]').forEach(function (btn) {
        var axis = self.model.axes[Number(btn.dataset.axis)];
        var curRaw = parts[axis.key];
        var cur = axis.choices.find(function (c) {
          return c.raw === curRaw;
        });
        var isNone = !curRaw || curRaw === axis.noneRaw;
        var on = false;
        if (btn.dataset.kind === 'add') on = btn.dataset.value === (isNone ? 'no' : 'yes');
        else if (btn.dataset.kind === 'color') on = !!cur && cur.color === btn.dataset.value;
        else if (btn.dataset.kind === 'qty') on = !!cur && cur.qty === btn.dataset.value;
        btn.classList.toggle('is-selected', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');

        // 選べない組み合わせ（在庫切れ・存在しない）は落とす
        if (btn.dataset.kind !== 'add') {
          var probe = Object.assign({}, parts);
          var probeChoice =
            btn.dataset.kind === 'color'
              ? axis.choices.find(function (c) {
                  return c.color === btn.dataset.value;
                })
              : axis.choices.find(function (c) {
                  return c.qty === btn.dataset.value;
                });
          probe[axis.key] = probeChoice ? probeChoice.raw : axis.noneRaw;
          var v = probeChoice ? self.lookup(probe) : null;
          var bad = !v || v.available === false;
          btn.classList.toggle('is-unavailable', bad);
        }
      });

      // 「追加する」を選ぶまでカラー・個数は隠す
      this.ui.querySelectorAll('[data-panel]').forEach(function (panel) {
        var axis = self.model.axes[Number(panel.dataset.axis)];
        var curRaw = parts[axis.key];
        var isNone = !curRaw || curRaw === axis.noneRaw;
        panel.hidden = isNone;
      });
      this.renumber();

      var summary = this.ui.querySelector('[data-summary]');
      if (summary) {
        var picked = this.model.axes
          .filter(function (a) {
            var curRaw = parts[a.key];
            return curRaw && curRaw !== a.noneRaw;
          })
          .map(function (a) {
            var c = a.choices.find(function (x) {
              return x.raw === parts[a.key];
            });
            return a.label + (c && c.color ? ' ' + c.color : '') + (c ? ' ' + c.qty : '');
          });
        summary.textContent = 'オプション：' + (picked.length ? picked.join(' ／ ') : 'なし');
      }
    }
  }

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }

  if (!customElements.get('option-steps')) {
    customElements.define('option-steps', OptionSteps);
  }
})();
